import { Injectable, OnDestroy } from '@angular/core';
import {
  Auth,
  User as FirebaseUser,
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
} from '@angular/fire/auth';
import { Firestore, doc, getDocFromServer } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { User } from '@models';

import { LogService } from '../helpers/log.service';
import { ToastService } from '../services/api/toast.service';
import { UserService } from '../services/api/user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private userServiceSubscription?: Subscription;
  private firebaseUserSubject$ = new BehaviorSubject<FirebaseUser | null | undefined>(undefined);
  firebaseUser$ = this.firebaseUserSubject$.asObservable();
  private userSubject$ = new BehaviorSubject<User | null | undefined>(undefined);
  user$ = this.userSubject$.asObservable();
  private googleLoginInProgress = false;
  private readonly pendingGoogleLoginKey = 'googleLoginInProgress';
  private recoveringInterruptedLogin = false;
  private authGeneration = 0;
  private tokenGeneration = 0;
  private latestRestoreGeneration = 0;
  private stopTokenListener: () => void;
  private readonly sessionIdentityError =
    'De Google-e-mailcontrole is niet beschikbaar of ongeldig. Dit kan gebeuren bij een eerste aanmelding. Meld je opnieuw aan met Google. Blijft het probleem bestaan? Neem contact op met support.';
  private readonly profileMissingError = 'Je gebruikersprofiel ontbreekt. Neem contact op met de beheerder en probeer daarna opnieuw.';
  private readonly connectionError = 'Je gegevens konden niet worden gecontroleerd. Controleer je verbinding en probeer opnieuw.';
  private readonly disabledAccountError = 'Je account is uitgeschakeld. Neem contact op met de beheerder.';
  private readonly profileDeniedError = 'Je hebt geen toegang tot je gebruikersprofiel. Neem contact op met de beheerder.';

  private checkErrorMessage(error: unknown, stage: 'token' | 'profile'): string {
    const code = error && typeof error === 'object' && 'code' in error ? error.code : undefined;
    if (stage === 'token' && code === 'auth/user-disabled') return this.disabledAccountError;
    if (stage === 'profile' && code === 'permission-denied') return this.profileDeniedError;
    return this.connectionError;
  }

  get user(): User | null | undefined {
    return this.userSubject$.getValue();
  }

  get isWlzAdmin() {
    return this.user?.roles?.wlzadmin;
  }

  get isGeauthenticeerd$() {
    return this.firebaseUser$.pipe(
      filter((user) => user != null),
      map(() => true)
    );
  }

  constructor(
    private auth: Auth,
    private logService: LogService,
    private userService: UserService,
    private router: Router,
    private db: Firestore,
    private toastService: ToastService
  ) {
    onAuthStateChanged(this.auth, (fbUser) => {
      if (this.googleLoginInProgress || this.recoveringInterruptedLogin || (fbUser && this.auth.currentUser !== fbUser)) {
        return;
      }
      if (sessionStorage.getItem(this.pendingGoogleLoginKey)) {
        this.recoveringInterruptedLogin = true;
        const recovery = fbUser ? signOut(this.auth) : Promise.resolve();
        recovery
          .then(() => {
            sessionStorage.removeItem(this.pendingGoogleLoginKey);
            this.recoveringInterruptedLogin = false;
            this.publishFirebaseUser(null);
            this.toastService.showError('Het inloggen is onderbroken. Probeer opnieuw in te loggen.');
          })
          .catch(() => {
            this.logService.danger('Afmelden na onderbroken Google-aanmelding is mislukt.');
            this.toastService.showError('Afmelden is mislukt. Vernieuw de pagina en probeer opnieuw in te loggen.');
          });
        return;
      }
      if (fbUser) return;
      this.tokenGeneration++;
      this.publishFirebaseUser(fbUser);
    });
    this.stopTokenListener = onIdTokenChanged(this.auth, (fbUser) => {
      if (fbUser !== this.auth.currentUser) return;
      const generation = ++this.tokenGeneration;
      if (!fbUser || this.googleLoginInProgress || this.recoveringInterruptedLogin ||
          this.auth.currentUser !== fbUser) return;
      void this.restoreSessionIdentity(fbUser, generation);
    });
  }

  private sessionIdentityEmail(claims: Record<string, unknown>): string {
    const identity = claims['wlzSessionIdentity'] as { email?: unknown; checkedAt?: unknown } | undefined;
    const firebase = claims['firebase'] as { sign_in_provider?: unknown } | undefined;
    const validEmail = (email: unknown): email is string => typeof email === 'string' && email.length <= 320 &&
      /^[^\s@]+@(intellicare\.nl|ecaretcs\.nl|buurtzorgnederland\.com|buurtzorg\.com|buurtzorg\.org)$/i.test(email);
    if (firebase?.sign_in_provider !== 'google.com') throw new Error(this.sessionIdentityError);
    if (!Object.prototype.hasOwnProperty.call(claims, 'wlzSessionIdentity')) {
      const email = claims['email'];
      if (claims['email_verified'] === true && validEmail(email)) return email;
      throw new Error(this.sessionIdentityError);
    }
    if (!identity || !validEmail(identity.email) || typeof identity.checkedAt !== 'number' ||
        !Number.isSafeInteger(identity.checkedAt) || identity.checkedAt <= 0) {
      throw new Error(this.sessionIdentityError);
    }
    return identity.email;
  }

  private async restoreSessionIdentity(fbUser: FirebaseUser, generation: number): Promise<void> {
    const authGeneration = this.authGeneration;
    this.latestRestoreGeneration = generation;
    this.userServiceSubscription?.unsubscribe();
    this.userSubject$.next(undefined);
    this.firebaseUserSubject$.next(undefined);
    let stage: 'token' | 'claim' | 'profile' = 'token';
    try {
      const { claims } = await fbUser.getIdTokenResult();
      if (generation !== this.tokenGeneration || this.googleLoginInProgress || this.auth.currentUser !== fbUser) return;
      stage = 'claim';
      const firebase = claims['firebase'] as { sign_in_provider?: unknown } | undefined;
      if (typeof firebase?.sign_in_provider !== 'string' || !firebase.sign_in_provider) {
        throw new Error(this.sessionIdentityError);
      }
      if (firebase?.sign_in_provider !== 'google.com') {
        this.publishFirebaseUser(fbUser);
        return;
      }
      const email = this.sessionIdentityEmail(claims);
      stage = 'profile';
      const snapshot = await getDocFromServer(doc(this.db, `users/${fbUser.uid}`));
      if (!snapshot.exists()) throw new Error(this.profileMissingError);
      if (generation !== this.tokenGeneration || this.googleLoginInProgress ||
          this.auth.currentUser !== fbUser) return;
      this.userSubject$.next({ ...snapshot.data() as User, email });
      this.firebaseUserSubject$.next(fbUser);
    } catch (error) {
      if (generation !== this.tokenGeneration || this.googleLoginInProgress || this.auth.currentUser !== fbUser) {
        // Firebase signs out an invalidated current user before rejecting its token request.
        if (stage === 'token' && this.auth.currentUser === null && !this.googleLoginInProgress &&
            !this.recoveringInterruptedLogin && authGeneration === this.authGeneration &&
            generation === this.latestRestoreGeneration && error && typeof error === 'object' &&
            'code' in error && error.code === 'auth/user-disabled') {
          console.warn('Controle van het aanmeldtoken is mislukt.');
          this.toastService.showError(this.disabledAccountError);
        }
        return;
      }
      this.userSubject$.next(null);
      this.firebaseUserSubject$.next(null);
      const identityError = stage === 'claim' && error instanceof Error &&
        error.message === this.sessionIdentityError;
      const missingProfile = stage === 'profile' && error instanceof Error && error.message === this.profileMissingError;
      if (!identityError) {
        console.warn(stage === 'token' ? 'Controle van het aanmeldtoken is mislukt.' : 'Controle van het gebruikersprofiel is mislukt.');
      }
      const message = identityError ? (error as Error).message : missingProfile ? this.profileMissingError
        : this.checkErrorMessage(error, stage === 'token' ? 'token' : 'profile');
      this.toastService.showError(message);
    }
  }

  private publishFirebaseUser(fbUser: FirebaseUser | null) {
    if (fbUser) {
      if (this.firebaseUserSubject$.value?.uid === fbUser.uid) return;
      this.logService.info('[onAuthStateChanged] gebruiker aangemeld');
      this.userServiceSubscription?.unsubscribe();
      this.firebaseUserSubject$.next(fbUser);
      this.userServiceSubscription = this.userService
        .getById(fbUser.uid)
        .pipe(
          filter((user) => !!user),
          take(1)
        )
        .subscribe((user) => this.userSubject$.next(user));
    } else {
      this.userServiceSubscription?.unsubscribe();
      this.logService.warning('[onAuthStateChanged] no user: ', fbUser);
      this.userSubject$.next(null);
      this.firebaseUserSubject$.next(null);
    }
  }

  async login(email: string, password: string) {
    const credentials = await signInWithEmailAndPassword(this.auth, email, password);
    return credentials;
  }

  async loginWithGoogle() {
    if (this.googleLoginInProgress) {
      throw new Error('Inloggen is al bezig. Probeer het zo nodig opnieuw.');
    }
    this.googleLoginInProgress = true;
    this.tokenGeneration++;
    sessionStorage.setItem(this.pendingGoogleLoginKey, 'true');
    const generation = ++this.authGeneration;
    this.userServiceSubscription?.unsubscribe();
    this.userSubject$.next(undefined);
    this.firebaseUserSubject$.next(undefined);
    const provider = new GoogleAuthProvider();
    let popupSucceeded = false;
    let popupUser: FirebaseUser | undefined;
    let keepGateClosed = false;
    let shouldRestore = true;
    let failureMessage: string | undefined;
    let stage: 'token' | 'claim' | 'profile' = 'token';
    try {
      const credentials = await signInWithPopup(this.auth, provider);
      popupSucceeded = true;
      popupUser = credentials.user;
      while (true) {
        const tokenGeneration = this.tokenGeneration;
        let claims: Record<string, unknown>;
        stage = 'token';
        try {
          ({ claims } = await credentials.user.getIdTokenResult());
        } catch (error) {
          if (generation === this.authGeneration && this.auth.currentUser === credentials.user &&
              tokenGeneration !== this.tokenGeneration) continue;
          throw error;
        }
        if (generation !== this.authGeneration || this.auth.currentUser !== credentials.user) {
          failureMessage = 'Inloggen is onderbroken. Probeer het opnieuw.';
          throw new Error(failureMessage);
        }
        if (tokenGeneration !== this.tokenGeneration) continue;
        stage = 'claim';
        let email: string;
        try {
          email = this.sessionIdentityEmail(claims);
        } catch {
          shouldRestore = false;
          this.userSubject$.next(null);
          this.firebaseUserSubject$.next(null);
          this.toastService.showError(this.sessionIdentityError);
          return null;
        }
        stage = 'profile';
        let snapshot;
        try {
          snapshot = await getDocFromServer(doc(this.db, `users/${credentials.user.uid}`));
        } catch (error) {
          if (generation === this.authGeneration && this.auth.currentUser === credentials.user &&
              tokenGeneration !== this.tokenGeneration) continue;
          throw error;
        }
        if (generation !== this.authGeneration || this.auth.currentUser !== credentials.user) {
          failureMessage = 'Inloggen is onderbroken. Probeer het opnieuw.';
          throw new Error(failureMessage);
        }
        if (tokenGeneration !== this.tokenGeneration) continue;
        if (!snapshot.exists()) {
          failureMessage = this.profileMissingError;
          throw new Error(failureMessage);
        }
        const user = snapshot.data() as User;
        user.email = email;
        this.userSubject$.next(user);
        this.firebaseUserSubject$.next(credentials.user);
        return credentials;
      }
    } catch (error) {
      if (popupSucceeded && !failureMessage && stage !== 'claim') {
        console.warn(stage === 'token' ? 'Controle van het aanmeldtoken is mislukt.' : 'Controle van het gebruikersprofiel is mislukt.');
        failureMessage = this.checkErrorMessage(error, stage);
      } else if (popupSucceeded && failureMessage === this.profileMissingError) {
        console.warn('Het gebruikersprofiel ontbreekt.');
      }
      if (generation === this.authGeneration) {
        if (popupSucceeded && this.auth.currentUser !== popupUser) {
          if (this.auth.currentUser) {
            void this.restoreSessionIdentity(this.auth.currentUser, ++this.tokenGeneration);
          }
        } else if (popupSucceeded) {
          try {
            await signOut(this.auth);
          } catch {
            // Keep the publication gate closed if Firebase cannot end the session.
            keepGateClosed = true;
            throw new Error('Inloggen is niet voltooid en afmelden is mislukt. Vernieuw de pagina en probeer opnieuw.');
          }
          this.userSubject$.next(null);
          this.firebaseUserSubject$.next(null);
        } else if (this.auth.currentUser) {
          void this.restoreSessionIdentity(this.auth.currentUser, ++this.tokenGeneration);
        } else {
          this.userSubject$.next(null);
          this.firebaseUserSubject$.next(null);
        }
      } else if (popupSucceeded && this.auth.currentUser === popupUser) {
        try {
          await signOut(this.auth);
        } catch {
          keepGateClosed = true;
        }
      }
      if (popupSucceeded) {
        throw new Error(
          failureMessage ||
            'Je e-mailadres kon niet worden gesynchroniseerd. Controleer je verbinding of neem contact op met de beheerder en probeer opnieuw.'
        );
      }
      throw error;
    } finally {
      if (!keepGateClosed) {
        sessionStorage.removeItem(this.pendingGoogleLoginKey);
        this.googleLoginInProgress = false;
        if (shouldRestore && this.auth.currentUser && !this.firebaseUserSubject$.value) {
          void this.restoreSessionIdentity(this.auth.currentUser, ++this.tokenGeneration);
        }
      }
    }
  }

  async register(email: string, password: string) {
    const result = await createUserWithEmailAndPassword(this.auth, email, password);
    await this.sendEmailVerification();
    return result;
  }

  async sendEmailVerification() {
    const user = this.auth.currentUser;
    if (user) {
      await sendEmailVerification(user);
      this.router.navigate(['/verify-email']);
    }
  }

  async sendPasswordResetEmail(passwordResetEmail: string) {
    return await sendPasswordResetEmail(this.auth, passwordResetEmail);
  }

  async logout() {
    this.authGeneration++;
    this.tokenGeneration++;
    await signOut(this.auth);
    this.userServiceSubscription?.unsubscribe();
    this.userSubject$.next(null);
    this.firebaseUserSubject$.next(null);
    this.router.navigate(['/']);
  }

  ngOnDestroy() {
    this.stopTokenListener();
    this.authGeneration++;
    this.tokenGeneration++;
    this.firebaseUserSubject$.unsubscribe();
    this.userSubject$.unsubscribe();
    if (this.userServiceSubscription) {
      this.userServiceSubscription.unsubscribe();
    }
  }
}
